import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Alert,
} from "@mui/material";
import DataObjectOutlined from "@mui/icons-material/DataObjectOutlined";
import TableChartOutlined from "@mui/icons-material/TableChartOutlined";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import { useTranslation } from "react-i18next";
import { downloadExampleFlowExcel, downloadExampleFlowJson } from "../../utils/chatbotFlowExchange";

const IMPORT_HELP = {
  json: {
    Icon: DataObjectOutlined,
    titleKey: "chatbotConfigPage.flowImportHelpJsonTitle",
    introKey: "chatbotConfigPage.flowImportHelpJsonIntro",
    bulletKeys: [
      "chatbotConfigPage.flowImportHelpJsonBullet1",
      "chatbotConfigPage.flowImportHelpJsonBullet2",
      "chatbotConfigPage.flowImportHelpJsonBullet3",
      "chatbotConfigPage.flowImportHelpJsonBullet4",
    ],
    hasExample: true,
  },
  excel: {
    Icon: TableChartOutlined,
    titleKey: "chatbotConfigPage.flowImportHelpExcelTitle",
    introKey: "chatbotConfigPage.flowImportHelpExcelIntro",
    bulletKeys: [
      "chatbotConfigPage.flowImportHelpExcelBullet1",
      "chatbotConfigPage.flowImportHelpExcelBullet2",
      "chatbotConfigPage.flowImportHelpExcelBullet3",
      "chatbotConfigPage.flowImportHelpExcelBullet4",
    ],
    hasExample: true,
  },
  pdf: {
    Icon: PictureAsPdfOutlined,
    titleKey: "chatbotConfigPage.flowImportHelpPdfTitle",
    introKey: "chatbotConfigPage.flowImportHelpPdfIntro",
    bulletKeys: [
      "chatbotConfigPage.flowImportHelpPdfBullet1",
      "chatbotConfigPage.flowImportHelpPdfBullet2",
      "chatbotConfigPage.flowImportHelpPdfBullet3",
      "chatbotConfigPage.flowImportHelpPdfBullet4",
      "chatbotConfigPage.flowImportHelpPdfBullet5",
      "chatbotConfigPage.flowImportHelpPdfBullet6",
    ],
    hasExample: false,
  },
};

export default function ChatbotFlowImportHelpDialog({ type, open, onClose, onSelectFile }) {
  const { t } = useTranslation("settings");
  const config = type ? IMPORT_HELP[type] : null;
  if (!config) return null;

  const { Icon, titleKey, introKey, bulletKeys, hasExample } = config;

  const handleDownloadExample = () => {
    if (type === "json") downloadExampleFlowJson();
    else if (type === "excel") downloadExampleFlowExcel();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Icon color="primary" fontSize="small" />
        {t(titleKey)}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t(introKey)}
        </Typography>

        <Typography variant="subtitle2" gutterBottom>
          {t("chatbotConfigPage.flowImportHelpHowItWorks")}
        </Typography>
        <List dense disablePadding sx={{ mb: 2 }}>
          {bulletKeys.map((key) => (
            <ListItem key={key} disableGutters sx={{ alignItems: "flex-start" }}>
              <ListItemText primary={t(key)} primaryTypographyProps={{ variant: "body2" }} />
            </ListItem>
          ))}
        </List>

        <Alert severity="warning" sx={{ mb: hasExample ? 2 : 0 }}>
          {t("chatbotConfigPage.flowImportHelpReplaceWarning")}
        </Alert>

        {hasExample ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("chatbotConfigPage.flowImportHelpExampleTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t("chatbotConfigPage.flowImportHelpExampleHint")}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadOutlined />}
              onClick={handleDownloadExample}
              sx={{ textTransform: "none" }}
            >
              {t("chatbotConfigPage.flowImportHelpDownloadExample")}
            </Button>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          {t("shared.cancel")}
        </Button>
        <Button
          variant="contained"
          startIcon={<FileUploadOutlined />}
          onClick={() => {
            onClose();
            onSelectFile?.(type);
          }}
          sx={{ textTransform: "none" }}
        >
          {t("chatbotConfigPage.flowImportHelpSelectFile")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
